import { Currency, Price } from "../../../index";

type BillingTotalsConstructorProps = {
	balance: number;
	credit: number;
	creditToBalance: number;
	currency: Currency;
	discount: number;
	grandTotal: number;
	tax: number;
	subTotal: number;
	total: number;
};

class BillingTotals {
	balance: number;
	credit: number;
	creditToBalance: number;
	currency: Currency;
	discount: number;
	grandTotal: number;
	tax: number;
	subTotal: number;
	total: number;

	constructor({
		balance,
		credit,
		creditToBalance,
		currency,
		discount,
		grandTotal,
		tax,
		subTotal,
		total,
	}: BillingTotalsConstructorProps) {
		this.balance = balance;
		this.credit = credit;
		this.creditToBalance = creditToBalance;
		this.currency = currency;
		this.discount = discount;
		this.grandTotal = grandTotal;
		this.tax = tax;
		this.subTotal = subTotal;
		this.total = total;
	}

	static fromPaddleAmount = (amount?: any) =>
		parseFloat((amount || 0).toString()) / 100;

	static toPaddleAmount = (amount?: number) => (amount || 0) * 100;

	static fromPaddleBillingTotals(json: any) {
		return new BillingTotals({
			balance: BillingTotals.fromPaddleAmount(json.balance),
			credit: BillingTotals.fromPaddleAmount(json.credit),
			creditToBalance: BillingTotals.fromPaddleAmount(json.creditToBalance),
			currency: json.currencyCode || Currency.defaultValue,
			discount: BillingTotals.fromPaddleAmount(json.discount),
			grandTotal: BillingTotals.fromPaddleAmount(json.grandTotal),
			tax: BillingTotals.fromPaddleAmount(json.tax),
			subTotal: BillingTotals.fromPaddleAmount(json.subTotal),
			total: BillingTotals.fromPaddleAmount(json.total),
		});
	}

	static fromJson(json: any) {
		return new BillingTotals({
			balance: BillingTotals.fromPaddleAmount(json.balance),
			credit: BillingTotals.fromPaddleAmount(json.credit),
			creditToBalance: BillingTotals.fromPaddleAmount(json.credit_to_balance),
			currency: json.currency_code || Currency.defaultValue,
			discount: BillingTotals.fromPaddleAmount(json.discount),
			grandTotal: BillingTotals.fromPaddleAmount(json.grand_total),
			tax: BillingTotals.fromPaddleAmount(json.tax),
			subTotal: BillingTotals.fromPaddleAmount(json.sub_total),
			total: BillingTotals.fromPaddleAmount(json.total),
		});
	}

	toJson() {
		const res = {
			balance: BillingTotals.toPaddleAmount(this.balance),
			credit: BillingTotals.toPaddleAmount(this.credit),
			credit_to_balance: BillingTotals.toPaddleAmount(this.creditToBalance),
			currency_code: this.currency,
			discount: BillingTotals.toPaddleAmount(this.discount),
			grand_total: BillingTotals.toPaddleAmount(this.grandTotal),
			tax: BillingTotals.toPaddleAmount(this.tax),
			sub_total: BillingTotals.toPaddleAmount(this.subTotal),
			total: BillingTotals.toPaddleAmount(this.total),
		} as any;
		return res;
	}

	displayGrandTotal(showFree: boolean, showPrefix: boolean) {
		const price = new Price({
			amount: this.grandTotal,
			currency: this.currency,
		});
		const priceDisplay = price.display(showFree, showPrefix);
		return priceDisplay;
	}

	displayTotal(showFree: boolean, showPrefix: boolean) {
		const price = new Price({
			amount: this.total,
			currency: this.currency,
		});
		const priceDisplay = price.display(showFree, showPrefix);
		return priceDisplay;
	}

	merge(other: BillingTotals) {
		if (this.currency !== other.currency) {
			throw new Error("Cannot merge billing prices with different currencies");
		}
		return new BillingTotals({
			balance: this.balance + other.balance,
			credit: this.credit + other.credit,
			creditToBalance: this.creditToBalance + other.creditToBalance,
			currency: this.currency,
			discount: this.discount + other.discount,
			grandTotal: this.grandTotal + other.grandTotal,
			tax: this.tax + other.tax,
			subTotal: this.subTotal + other.subTotal,
			total: this.total + other.total,
		});
	}
}

export { BillingTotals };
